import {
  hasVapidKey,
  homeScreenOnly,
  pushNote,
  pushState,
  pushToggle,
  readDevice,
  useCustomerAuth,
  type PushState,
} from '@agos/customer-core'
import { T, tt } from '@agos/shared'
import { Bell } from 'lucide-react'
import { useState } from 'react'

/**
 * What the restaurant may send this customer, and through which channel.
 *
 * Three switches with three different owners, which is the whole reason this section is fiddly:
 *
 * - **Push** is the browser's, and the server's. It needs a permission *and* a subscription *and* a
 *   VAPID key, and `pushState` in `@agos/customer-core` is the state machine that keeps the four
 *   outcomes apart — FB-C03, where legacy reports `granted-active` for 114 browsers subscribed to a
 *   channel with no key to send on.
 * - **Marketing e-mail** and **marketing SMS** are the customer's, stored on their record and applied
 *   by the server when a campaign runs.
 *
 * **Both marketing flags default to on.** Legacy reads `?? true`, and the column is nullable, so a
 * customer who has never seen this screen is opted in. That is a consent question rather than a coding
 * one and the port does not quietly change it — but it *is* the reason the transactional note below
 * matters: a customer switching everything off must still be told their order is on its way.
 *
 * Toggling writes to the customer's own record. Nothing here sends anything, which is why it is not one
 * of BLOCKED.md's send paths.
 */
export function NotificationPreferences({
  publicKey,
  hasSubscription,
  pushStateOverride,
  pushBusy = false,
  pushProblem = '',
  onPush,
  marketingEmail,
  marketingSms,
  saving,
  onChange,
}: {
  publicKey: string | null
  hasSubscription: boolean
  /**
   * The state as the page already computed it — §9.239.
   *
   * The profile holds `usePush`, which owns the permission *and* keeps it current across a prompt, so
   * it hands the answer down rather than having this component recompute it from a permission read
   * once at mount. Absent, the component decides for itself, which is what every other caller does.
   */
  pushStateOverride?: PushState
  pushBusy?: boolean
  pushProblem?: string
  /** Absent means the row stays a statement, exactly as it was before §9.239. */
  onPush?: (enable: boolean) => void
  marketingEmail: boolean
  marketingSms: boolean
  saving: boolean
  onChange: (field: 'marketingEmailOptIn' | 'marketingSmsOptIn', value: boolean) => void
}) {
  const [browser] = useState(browserState)
  const [device] = useState(readDevice)
  const signedIn = useCustomerAuth((auth) => auth.token !== null)
  const state: PushState =
    pushStateOverride ??
    pushState({
      supported: browser.supported,
      // §9.734 - an iPhone tab is told how to get push rather than that it cannot have it.
      homeScreenOnly: homeScreenOnly(device),
      publicKey,
      /* §9.648 - read from the store rather than passed as `true`. This screen is behind a session, so a literal
         would be right today and is exactly the shape §9.239 found here before: *"the profile held a literal
         `false`"*. A sign-out while the page is open changes the answer, and the store knows. */
      signedIn: signedIn,
      permission: browser.permission,
      hasSubscription,
    })
  const toggle = pushToggle(state, pushBusy)
  const switchable = onPush !== undefined && toggle.shown

  return (
    <section data-notification-preferences>
      <div className="flex items-center gap-2 mb-3">
        <Bell className="w-4 h-4 text-muted-foreground" />
        <h2 className="font-semibold text-foreground">
          <T>Notifikácie</T>
        </h2>
      </div>
      <div className="rounded-2xl border border-border p-4 space-y-3">
        {/* §9.734, FB-1036 - **drawn in every state.** It was inside `state !== 'unsupported'`, legacy's own
            gate, so an iPhone in Safari - which has no `PushManager` outside a home-screen app - showed no push
            row at all and the customer could not tell *this phone cannot* from *this restaurant does not*. Each
            state carries its sentence; `home-screen` says how to get push on that phone. */}
        <div className="flex items-center justify-between gap-3" data-push-state={state}>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-foreground">
              <T>Push notifikácie</T>
            </p>
            {/* The state's own sentence, rather than a guess from the checkbox. `unconfigured` says
                the server has no key - which is true here and is not the customer's doing. */}
            <p className="text-xs text-muted-foreground mt-0.5" data-push-note>
              {pushNote(state)}
            </p>
            {!hasVapidKey(publicKey) && <span data-push-unconfigured />}
            {pushProblem !== '' && (
              <p className="text-xs text-red-500 mt-1" data-push-problem>
                {pushProblem}
              </p>
            )}
          </div>
          {/* A switch where enabling can lead somewhere, a statement where it cannot - §9.239.
              `unconfigured`, `unsupported` and `home-screen` keep the label, because a checkbox that
              sometimes opens a permission dialog and sometimes does nothing is worse than a
              statement of fact; `granted-active` gets the box, because switching **off** needs no
              prompt and always works, and legacy has had it since the port. */}
          {switchable ? (
            <input
              type="checkbox"
              data-push-switch
              aria-label={tt('Push notifikácie')}
              checked={toggle.checked}
              disabled={toggle.disabled}
              onChange={(event) => onPush?.(event.target.checked)}
              className="w-5 h-5 rounded accent-primary cursor-pointer flex-shrink-0 disabled:cursor-not-allowed"
            />
          ) : (
            <span
              className={`text-xs font-semibold shrink-0 ${state === 'granted-active' ? 'text-emerald-600' : 'text-muted-foreground'}`}
              data-push-summary
            >
              {state === 'granted-active' ? <T>Zapnuté</T> : <T>Vypnuté</T>}
            </span>
          )}
        </div>
        <div className="border-t border-border" />

        <Toggle
          name="marketing-email"
          label={tt('Marketingové emaily')}
          hint={tt('Kupóny, akcie, vernostné kampane')}
          checked={marketingEmail}
          disabled={saving}
          onChange={(value) => onChange('marketingEmailOptIn', value)}
        />
        <div className="border-t border-border" />
        <Toggle
          name="marketing-sms"
          label={tt('Marketingové SMS')}
          hint={tt('Reaktivačné správy, kupóny')}
          checked={marketingSms}
          disabled={saving}
          onChange={(value) => onChange('marketingSmsOptIn', value)}
        />

        <p className="text-[11px] text-muted-foreground italic pt-1">
          <T>
            Transakčné notifikácie (potvrdenie objednávky, status doručenia, reset hesla) sa odosielajú
            vždy.
          </T>
        </p>
      </div>
    </section>
  )
}

function Toggle(props: {
  name: string
  label: string
  hint: string
  checked: boolean
  disabled: boolean
  onChange: (value: boolean) => void
}) {
  return (
    <label className="flex items-center justify-between gap-3 cursor-pointer">
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium text-foreground">{props.label}</span>
        <span className="block text-xs text-muted-foreground mt-0.5">{props.hint}</span>
      </span>
      <input
        type="checkbox"
        data-pref={props.name}
        checked={props.checked}
        disabled={props.disabled}
        onChange={(event) => props.onChange(event.target.checked)}
        className="w-5 h-5 rounded accent-primary cursor-pointer flex-shrink-0"
      />
    </label>
  )
}

/** The browser's half of the push answer, read once — none of it changes without a user action. */
function browserState(): { supported: boolean; permission: NotificationPermission } {
  const supported =
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window
  return { supported, permission: supported ? Notification.permission : 'denied' }
}
